import dayjs from "dayjs";
import {
  ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS,
  ROUTE_SUPER_ADMIN_ENQUIRIES,
  ROUTE_SUPER_ADMIN_EVENT_PHOTOS,
  ROUTE_SUPER_ADMIN_EVENT_TYPES,
  ROUTE_SUPER_ADMIN_EVENTS,
  ROUTE_SUPER_ADMIN_USERS,
  ROUTE_SUPER_ADMIN_VENUES,
  ROUTE_SUPER_ADMIN_VENDORS,
  ROUTE_SUPER_ADMIN_VISITOR_LOGS,
} from "../../../Util/VMConstants";

/** Map API module keys / frontend_route to app router paths. */
export const MODULE_ROUTE_MAP = {
  venues: ROUTE_SUPER_ADMIN_VENUES,
  users: ROUTE_SUPER_ADMIN_USERS,
  enquiries: ROUTE_SUPER_ADMIN_ENQUIRIES,
  records: ROUTE_SUPER_ADMIN_ENQUIRIES,
  events: ROUTE_SUPER_ADMIN_EVENTS,
  "event-types": ROUTE_SUPER_ADMIN_EVENT_TYPES,
  event_types: ROUTE_SUPER_ADMIN_EVENT_TYPES,
  "visitor-logs": ROUTE_SUPER_ADMIN_VISITOR_LOGS,
  visitor_logs: ROUTE_SUPER_ADMIN_VISITOR_LOGS,
  "event-photos": ROUTE_SUPER_ADMIN_EVENT_PHOTOS,
  event_photos: ROUTE_SUPER_ADMIN_EVENT_PHOTOS,
  vendors: ROUTE_SUPER_ADMIN_VENDORS,
  "event-vendors": ROUTE_SUPER_ADMIN_VENDORS,
  event_vendors: ROUTE_SUPER_ADMIN_VENDORS,
  "deleted-accounts": ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS,
  deleted_accounts: ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS,
};

const FRONTEND_ROUTE_ALIASES = {
  venues: ROUTE_SUPER_ADMIN_VENUES,
  users: ROUTE_SUPER_ADMIN_USERS,
  enquiries: ROUTE_SUPER_ADMIN_ENQUIRIES,
  events: ROUTE_SUPER_ADMIN_EVENTS,
  "event-types": ROUTE_SUPER_ADMIN_EVENT_TYPES,
  "visitor-logs": ROUTE_SUPER_ADMIN_VISITOR_LOGS,
  "event-photos": ROUTE_SUPER_ADMIN_EVENT_PHOTOS,
  "event-vendors": ROUTE_SUPER_ADMIN_VENDORS,
  vendors: ROUTE_SUPER_ADMIN_VENDORS,
  "deleted-accounts": ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS,
};

export const DASHBOARD_CARD_COLORS = {
  blue: "#1565c0",
  green: "#2e7d32",
  orange: "#ef6c00",
  purple: "#7b1fa2",
  red: "#c62828",
  teal: "#00838f",
};

const URL_FILTER_KEYS = [
  "country",
  "state",
  "city",
  "from",
  "from_date",
  "to",
  "to_date",
  "search",
  "status",
  "venue_status",
  "profile",
  "venue_type",
  "has_subscription",
  "subscription",
  "subscription_expiry",
  "subscription_purchased_today",
  "venue_id",
  "venue_user_id",
];

export function resolveModulePath(navigate) {
  if (!navigate) return null;

  const moduleKey = navigate.module;
  if (moduleKey && MODULE_ROUTE_MAP[moduleKey]) {
    return `/${MODULE_ROUTE_MAP[moduleKey]}`;
  }

  const route = String(navigate.frontend_route || "").replace(/^\//, "");
  if (route && FRONTEND_ROUTE_ALIASES[route]) {
    return `/${FRONTEND_ROUTE_ALIASES[route]}`;
  }

  if (route) return `/superAdmin/${route}`;
  return null;
}

export function buildModuleNavigateUrl(navigate) {
  const path = resolveModulePath(navigate);
  if (!path) return null;

  const query = navigate.query || {};
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== "") params.set(key, String(value));
  });

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function hasUrlListFilters(searchParams) {
  return URL_FILTER_KEYS.some((key) => {
    const value = searchParams.get(key);
    return value != null && value !== "";
  });
}

export function locationOverrideFromSearchParams(searchParams) {
  const params = {};
  const country = searchParams.get("country");
  const state = searchParams.get("state");
  const city = searchParams.get("city");
  const venueId = searchParams.get("venue_id");
  if (country) params.country = country;
  if (state) params.state = state;
  if (city) params.city = city;
  if (venueId) params.venue_id = venueId;
  return params;
}

/** Parse dashboard / list URL query into state overrides for list pages. */
export function getUrlFilterOverrides(searchParams) {
  const get = (key) => searchParams.get(key) || "";

  const subscriptionRaw = get("subscription") || get("has_subscription");
  let subscription = "";
  if (["yes", "no", "active", "expired"].includes(subscriptionRaw)) {
    subscription = subscriptionRaw;
  } else if (subscriptionRaw === "true" || subscriptionRaw === "with") {
    subscription = "yes";
  } else if (subscriptionRaw === "false" || subscriptionRaw === "without") {
    subscription = "no";
  }

  const profile = get("profile");
  const status = get("status");
  const venueStatus = get("venue_status");

  const listFilters = {
    status: status === "all" ? "" : status,
    venue_status: venueStatus === "all" ? "" : venueStatus,
    subscription,
    profile: profile === "all" ? "" : profile,
    venue_type: get("venue_type") === "all" ? "" : get("venue_type"),
    subscription_expiry: get("subscription_expiry"),
    subscription_purchased_today: get("subscription_purchased_today"),
  };

  const skipRegistrationDates = hasSubscriptionListFilter(listFilters);
  const fromStr = skipRegistrationDates ? "" : get("from") || get("from_date");
  const toStr = skipRegistrationDates ? "" : get("to") || get("to_date");

  let selectedCard = "total";
  if (profile && profile !== "all") {
    selectedCard = profile.charAt(0).toUpperCase() + profile.slice(1).toLowerCase();
    if (selectedCard === "Premium" || selectedCard === "Trial" || selectedCard === "Free") {
      // keep
    } else {
      selectedCard = profile;
    }
  } else if (listFilters.subscription_expiry) {
    selectedCard = `expiry:${listFilters.subscription_expiry}`;
  } else if (listFilters.subscription_purchased_today === "yes") {
    selectedCard = "subscription_purchased_today";
  } else if (venueStatus === "active") {
    selectedCard = "active_venue_count";
  } else if (status === "active") {
    selectedCard = "active_count";
  } else if (status === "inactive") {
    selectedCard = "inactive_count";
  } else if (subscription === "yes") {
    selectedCard = "with_subscription";
  } else if (subscription === "no") {
    selectedCard = "without_subscription";
  } else if (subscription === "active") {
    selectedCard = "active_subscription";
  }

  const venueType = listFilters.venue_type;
  if (venueType) {
    selectedCard = `type:${venueType}`;
  }

  return {
    listFilters,
    locationOverride: locationOverrideFromSearchParams(searchParams),
    fromDate: fromStr ? dayjs(fromStr) : null,
    toDate: toStr ? dayjs(toStr) : null,
    search: get("search"),
    selectedCard,
  };
}

export function cardBackground(color) {
  return DASHBOARD_CARD_COLORS[color] || DASHBOARD_CARD_COLORS.blue;
}

export function formatDashboardCardValue(card) {
  if (card?.id === "today_subscriptions_amount") {
    const num = Number(card.value);
    return Number.isFinite(num) ? num.toLocaleString() : card.value ?? 0;
  }
  return card?.value ?? 0;
}

/** Build sections from flat stats when API omits sections (back-compat). */
export function buildFallbackSections(data = {}) {
  const subs = data.today_subscriptions || {};
  const expiry = data.subscriptions_expiry || {};

  const summaryCards = [
    { id: "total_venues", label: "Total Venues", value: data.total_venues, color: "green", clickable: true, navigate: { module: "venues", query: { venue_status: "active" } } },
    { id: "total_users", label: "Total Users", value: data.total_users, color: "green", clickable: true, navigate: { module: "users" } },
    { id: "total_records", label: "Total Records", value: data.total_records, color: "green", clickable: true, navigate: { module: "enquiries" } },
    { id: "total_events", label: "Total Events", value: data.total_events, color: "green", clickable: true, navigate: { module: "events" } },
    { id: "total_event_types", label: "Total Event Types", value: data.total_event_types, color: "green", clickable: true, navigate: { module: "event-types" } },
    { id: "trial_venues", label: "Trial Venues", value: data.trial_venues, color: "purple", clickable: true, navigate: { module: "venues", query: { profile: "trial" } } },
    { id: "free_venues", label: "Free Venues", value: data.free_venues, color: "orange", clickable: true, navigate: { module: "venues", query: { profile: "free" } } },
    { id: "premium_venues", label: "Premium Venues", value: data.premium_venues, color: "blue", clickable: true, navigate: { module: "venues", query: { profile: "premium" } } },
    { id: "delete_account_requests", label: "Delete Account Requests", value: data.delete_account_requests, color: "red", clickable: true, navigate: { module: "deleted-accounts", query: { status: "all" } } },
  ];

  return [
    { id: "summary", title: "Summary", cards: summaryCards },
    {
      id: "modules",
      title: "Modules",
      cards: [
        { id: "total_visitor_logs", label: "Visitor Logs", value: data.total_visitor_logs, color: "blue", clickable: true, navigate: { module: "visitor-logs" } },
        { id: "total_event_photos", label: "Event Photos", value: data.total_event_photos, color: "blue", clickable: true, navigate: { module: "event-photos" } },
        { id: "total_vendors", label: "Event Vendors", value: data.total_vendors, color: "blue", clickable: true, navigate: { module: "event-vendors" } },
      ],
    },
    {
      id: "today_stats",
      title: "Today Stats",
      cards: [
        { id: "today_subscriptions_amount", label: "Today Subscriptions amount", value: subs.amount, color: "blue", clickable: true, navigate: { module: "users", query: { subscription_purchased_today: "yes" } } },
        { id: "today_subscriptions_count", label: "Today Subscriptions Count", value: subs.count, color: "orange", clickable: true, navigate: { module: "users", query: { subscription_purchased_today: "yes" } } },
      ],
    },
    {
      id: "subscriptions_expiry",
      title: "Subscriptions Expiry",
      cards: [
        { id: "subscriptions_expiry_today", label: "Today", value: expiry.today, color: "blue", clickable: true, navigate: { module: "users", query: { subscription_expiry: "today" } } },
        { id: "subscriptions_expiry_this_week", label: "This Week", value: expiry.this_week, color: "blue", clickable: true, navigate: { module: "users", query: { subscription_expiry: "this_week" } } },
        { id: "subscriptions_expiry_this_month", label: "This Month", value: expiry.this_month, color: "green", clickable: true, navigate: { module: "users", query: { subscription_expiry: "this_month" } } },
        { id: "subscriptions_expiry_expired", label: "Expired", value: expiry.expired, color: "red", clickable: true, navigate: { module: "users", query: { subscription_expiry: "expired" } } },
      ],
    },
  ];
}

const REGISTRATION_DATE_KEYS = ["from", "from_date", "to", "to_date"];

export function hasSubscriptionListFilter(listFilters = {}) {
  return Boolean(listFilters.subscription_expiry || listFilters.subscription_purchased_today);
}

/** Subscription expiry / today-purchase counts are not scoped by registration date range. */
export function shouldApplyClientListFilter(listFilters = {}) {
  return !hasSubscriptionListFilter(listFilters);
}

function stripRegistrationDates(filters = {}) {
  const next = { ...filters };
  REGISTRATION_DATE_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

export function mergeNavigateQuery(navigate, filtersApplied = {}) {
  if (!navigate) return navigate;
  const cardQuery = navigate.query || {};
  const applied = hasSubscriptionListFilter(cardQuery)
    ? stripRegistrationDates(filtersApplied)
    : filtersApplied;

  return {
    ...navigate,
    query: {
      ...applied,
      ...cardQuery,
    },
  };
}
